import React, { useEffect, useState } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import { LightBox } from "@liquid-design/liquid-design-react";
import {
  LdButton,
  LdIcon,
  LdOption,
  LdSelect,
  LdTypo,
  LdTooltip,
  LdTabs,
  LdTablist,
  LdTabpanellist,
  LdTab,
  LdTabpanel,
  LdLabel,
} from "@emdgroup-liquid/liquid/dist/react";
import { AppLayout } from "layouts";

import { useDispatch, useSelector } from "store";
import survey, {
  endSurvey,
  getSurveyById,
  getSurveyFeedback,
  updateParticipants,
  selectSurveyById,
  selectIterationsForSurvey,
  selectQuestionsForSurvey,
  selectFeedbackForIteration,
} from "services/survey";

import AnswersSummary from "./components/AnswersSummary";
import AddUserForm from "components/AddUserForm/AddUserForm";
import { UserPlusIcon, CloneIcon } from "components/Icons";
import { getIterationDate } from "util/date";
import { SurveyFrequency } from "typings/app/types";
import { QuestionForm } from "components/Question";
import AnswerHistory from "./components/AnswerHistory";

const Survey = () => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [renderAnswers, setRenderAnswers] = useState(false);
  const [selectedIteration, setSelectedIteration] = useState<number>();
  const { surveyId = "" } = useParams();
  const { feedback, status } = useSelector(({ survey }) => survey);
  const selected = useSelector(selectSurveyById(surveyId));
  const questions = useSelector(selectQuestionsForSurvey(surveyId));
  const iterations = useSelector(selectIterationsForSurvey(surveyId));
  const currentFeedback = useSelector(
    selectFeedbackForIteration(selectedIteration)
  );
  useEffect(() => {
    if (surveyId !== undefined) {
      dispatch(getSurveyById(surveyId));
      dispatch(getSurveyFeedback(surveyId));
    }
  }, [surveyId]);

  useEffect(() => {
    if (selected !== undefined) {
      setSelectedIteration(selected.currentIteration);
      if (!selected.participants?.length) {
        setModal("participants");
      }
    }
  }, [selected]);

  useEffect(() => {
    setRenderAnswers(activeTab === 1);
  }, [activeTab]);

  if (selected === undefined) return null;

  const currentIteration = iterations[selected.currentIteration];

  const handleChangeParticipants = (participants: string[]) => {
    if (surveyId !== undefined) {
      dispatch(updateParticipants({ surveyId, participants }));
    }
    setModal(null);
  };

  const confirmEndSurvey = () => {
    setModal(null);
    dispatch(endSurvey(surveyId));
  };

  const handleTabChange = (ev: CustomEvent<string>) => {
    const tabIndex = ev.detail.split("-").pop();
    setActiveTab(tabIndex ? parseInt(tabIndex) : 0);
  };

  const shareUrl = () =>
    `${window.location.protocol}//${window.location.host}/answer?survey=${surveyId}`;
  return (
    <AppLayout>
      <div className="container flex mx-auto flex-col items-center pt-ld-32 px-ld-16 md:px-ld-32">
        <div className="w-full">
          <LdTypo variant="h1">{selected?.surveyName}</LdTypo>
          {currentIteration && (
            <LdTypo>
              Current Iteration ends {moment(currentIteration.endsAt).fromNow()}
            </LdTypo>
          )}
          <LdTypo>
            Survey ends at {moment(selected?.endDate).format("ddd, DD.MM.YYYY")}
          </LdTypo>
          <div className="flex flex-row w-full gap-4 md:justify-end md:items-end my-ld-16">
            <LdButton
              size="sm"
              onClick={() => setModal("endSurvey")}
              mode="danger"
            >
              End Survey
            </LdButton>
            <LdButton
              size="sm"
              mode="secondary"
              onClick={() => setModal("participants")}
            >
              <LdIcon>
                <UserPlusIcon />
              </LdIcon>
              Invite Participants
            </LdButton>
            <LdTooltip triggerType="click">
              <LdButton
                slot="trigger"
                size="sm"
                onClick={() => navigator.clipboard.writeText(shareUrl())}
                mode="secondary"
              >
                <LdIcon>
                  <CloneIcon />
                </LdIcon>
                <span>Share URL</span>
              </LdButton>
              <LdTypo>Copied to clipboard</LdTypo>
            </LdTooltip>
          </div>
        </div>
        <LdTabs className="w-full" onTabChange={handleTabChange}>
          <LdTablist>
            <LdTab selected={activeTab === 0}>Preview</LdTab>
            <LdTab selected={activeTab === 1} disabled={!feedback}>
              Results
            </LdTab>
            <LdTab selected={activeTab === 2} disabled={!feedback}>
              History
            </LdTab>
          </LdTablist>
          <LdTabpanellist>
            <LdTabpanel>
              {questions !== undefined && (
                <div className="container flex flex-col divide-y-2 divide-neutral-010 my-ld-32">
                  {questions.map((question, index) => (
                    <QuestionForm
                      key={question.questionId}
                      questionType={question?.questionType || ""}
                      questionText={question.questionText}
                      options={question.options || []}
                      mandatory={question.mandatory}
                      onChange={() => {}}
                    />
                  ))}
                </div>
              )}
            </LdTabpanel>
            <LdTabpanel>
              {!!currentFeedback && (
                <div className="w-full my-ld-32">
                  {selected.frequency !== SurveyFrequency.once && (
                    <LdLabel position="left">
                      <span>Iteration</span>
                      <LdSelect
                        size="sm"
                        onInput={
                          ((
                            event: React.SyntheticEvent<
                              HTMLInputElement,
                              CustomEvent<string[]>
                            >
                          ) => {
                            setSelectedIteration(
                              parseInt(event.nativeEvent.detail[0])
                            );
                          }) as unknown as any
                        }
                        preventDeselection
                        className="w-full md:w-max"
                      >
                        {Object.values(iterations).map(
                          ({ startsAt, iteration }, index, arr) => (
                            <LdOption
                              key={iteration}
                              onSelect={(ev) => {
                                console.log("select");
                                setSelectedIteration(
                                  parseInt(ev.currentTarget.value)
                                );
                              }}
                              value={iteration.toString()}
                              selected={
                                iteration === selectedIteration ||
                                arr.length === 1
                              }
                            >
                              {getIterationDate(startsAt, selected.frequency)}
                            </LdOption>
                          )
                        )}
                      </LdSelect>
                    </LdLabel>
                  )}
                  <div className="flex flex-col divide-y-2 divide-neutral-100">
                    {currentFeedback.map(
                      ({
                        questionType = "",
                        questionText,
                        feedback,
                        options,
                        questionId,
                      }) => {
                        return (
                          <div key={questionId} className="py-ld-16">
                            <LdTypo className="mb-ld-8">
                              <span className="font-black">{questionText}</span>{" "}
                              ({feedback?.answers?.length} Answer
                              {feedback?.answers?.length !== 1 ? "s" : ""})
                            </LdTypo>
                            {(feedback?.answers?.length || 0) > 0 && (
                              <div className="flex justify-center md:justify-start py-ld-16">
                                {renderAnswers && (
                                  <AnswersSummary
                                    options={options || []}
                                    questionType={questionType}
                                    answers={feedback?.answers}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}
            </LdTabpanel>
            <LdTabpanel>
              <div className="w-full my-ld-32">
                <div className="flex flex-col divide-y-2 divide-neutral-100">
                  {feedback &&
                    feedback.map(
                      ({
                        questionType = "",
                        questionText,
                        history,
                        options,
                        questionId,
                      }) => {
                        return (
                          <div key={questionId} className="py-ld-16">
                            <LdTypo className="mb-ld-8">
                              <span className="font-black">{questionText}</span>
                            </LdTypo>
                            <div className="flex justify-center md:justify-start py-ld-16">
                              <AnswerHistory
                                questionType={questionType}
                                answerOptions={options || []}
                                history={history}
                                iterations={iterations}
                                labelSelector={(iteration) =>
                                  getIterationDate(
                                    iteration?.startsAt,
                                    selected.frequency
                                  ) || ""
                                }
                              />
                            </div>
                          </div>
                        );
                      }
                    )}
                </div>
              </div>
            </LdTabpanel>
          </LdTabpanellist>
        </LdTabs>
      </div>
      <LightBox
        className="ld-theme-bubblegum"
        open={modal === "participants"}
        onClose={() => setModal(null)}
      >
        <AddUserForm
          users={selected?.participants}
          onCancel={() => setModal(null)}
          onSubmit={handleChangeParticipants}
        />
      </LightBox>
      <LightBox
        className="ld-theme-bubblegum"
        open={modal === "endSurvey"}
        onClose={() => setModal(null)}
      >
        <LdTypo variant="h2">End Survey now?</LdTypo>
        <LdTypo>
          Do you really want to end this this Survey? After that, participants
          won't be able to submit any answers.
        </LdTypo>
        <div className="flex flex-row mt-ld-32 gap-4 justify-end">
          <LdButton
            className="flex-grow md:grow-0"
            mode="secondary"
            onClick={() => setModal(null)}
          >
            No, I'm good
          </LdButton>
          <LdButton mode="danger" onClick={confirmEndSurvey}>
            Yes, End Survey
          </LdButton>
        </div>
      </LightBox>
    </AppLayout>
  );
};

export default Survey;
